/**
 * The signatures a letter can be signed with, and how to recognise one.
 *
 * In code, like the presets and for the same reason: a signature that lived in
 * the database would be half-present on one environment and would need a
 * migration nobody remembers to run. The cost is honest and worth naming - a
 * new variant, or a changed job title, arrives with a deploy rather than with an
 * edit in the interface.
 *
 * React-free so the presets, the picker and the tests can all import it.
 *
 * AVATAR IS NOT PART OF A VARIANT. Applying one fills the four text fields and
 * leaves the picture alone: an operator who put a photo on the block chose that
 * separately, and losing it to a dropdown they used to fix a job title would be
 * a surprise. It is also why `match` ignores the avatar - a signature with a
 * photo still IS the WebAdmin signature.
 */

/** The fields a variant sets. Exactly the `signature` block's text props. */
export interface SignatureFields {
  name: string;
  title: string;
  email: string;
  phone: string;
}

export interface SignatureVariant {
  id: string;
  /** i18n suffix: `marketing.builder.signature.variant.<key>`. */
  key: string;
  fields: SignatureFields;
}

/**
 * The list, most-used first.
 *
 * WebAdmin leads because that is who the outreach letters are from: the agency
 * sends them and the legal identification attached to every campaign names the
 * agency. Husly is for letters to people who already use the product, where
 * signing as the agency would read as a stranger writing about their own tool.
 */
export const SIGNATURES: SignatureVariant[] = [
  {
    id: "webadmin",
    key: "webadmin",
    fields: { name: "Aimar Laksberg", title: "WebAdmin OÜ", email: "aimar@webadmin.ee", phone: "" },
  },
  {
    id: "webadmin-short",
    key: "webadminShort",
    fields: { name: "Aimar Laksberg", title: "WebAdmin OÜ", email: "", phone: "" },
  },
  {
    id: "husly",
    key: "husly",
    fields: { name: "Aimar Laksberg", title: "Husly", email: "aimar@husly.app", phone: "" },
  },
  {
    id: "name-only",
    key: "nameOnly",
    fields: { name: "Aimar Laksberg", title: "", email: "", phone: "" },
  },
];

/** What every preset signs with, and what the block starts as. */
export const DEFAULT_SIGNATURE: SignatureVariant = SIGNATURES[0]!;

/** The id the picker shows when the block matches no variant. Not a variant:
 *  a hand-typed signature is a real state, and a dropdown that goes blank
 *  instead of naming it looks broken. */
export const CUSTOM_SIGNATURE_ID = "custom";

export function findSignature(id: string): SignatureVariant | null {
  return SIGNATURES.find((s) => s.id === id) ?? null;
}

/** Trailing spaces are typing, not intent, so they do not make a signature
 *  custom. Missing keys read as empty for the same reason. */
function norm(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * Which variant a block's props are, or `custom`.
 *
 * The picker needs this on every render: the value it shows has to be derived
 * from the block, not remembered next to it. A stored "which variant" flag
 * would go stale the moment somebody edited the email field by hand, and then
 * the dropdown would claim a signature the letter does not carry.
 */
export function matchSignature(props: Record<string, unknown>): string {
  const hit = SIGNATURES.find(
    (s) =>
      norm(props.name) === s.fields.name &&
      norm(props.title) === s.fields.title &&
      norm(props.email) === s.fields.email &&
      norm(props.phone) === s.fields.phone,
  );
  return hit?.id ?? CUSTOM_SIGNATURE_ID;
}
